import { useEffect, useState } from 'react';

export interface KeyboardInset {
  /** Height of the on-screen keyboard covering the bottom of the layout viewport, in px (0 if none). */
  inset: number;
  /** Visible height (visualViewport.height) while the keyboard is open; null otherwise. */
  visibleHeight: number | null;
}

const NONE: KeyboardInset = { inset: 0, visibleHeight: null };
/** Smaller gaps are browser toolbars sliding in and out, not a keyboard. */
const MIN_KEYBOARD_PX = 80;

/**
 * iPhone Safari (and Android Chrome without `interactive-widget=resizes-content`) shows the keyboard
 * over the layout viewport, so `position: fixed; bottom: 0` ends up behind it and `100dvh` doesn't
 * shrink. visualViewport does: the keyboard covers innerHeight - (height + offsetTop).
 */
export function useKeyboardInset(active: boolean): KeyboardInset {
  const [state, setState] = useState<KeyboardInset>(NONE);
  useEffect(() => {
    const vv = typeof window === 'undefined' ? undefined : window.visualViewport;
    if (!active || !vv) return;
    const update = () => {
      // Pinch zoom also shrinks the visual viewport; that isn't a keyboard.
      const covered = vv.scale > 1.01 ? 0 : window.innerHeight - vv.height - vv.offsetTop;
      setState((prev) => {
        const next =
          covered < MIN_KEYBOARD_PX
            ? NONE
            : { inset: Math.round(covered), visibleHeight: Math.round(vv.height) };
        return prev.inset === next.inset && prev.visibleHeight === next.visibleHeight ? prev : next;
      });
    };
    update();
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    return () => {
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
      setState(NONE);
    };
  }, [active]);
  return state;
}
